import { useEffect, useRef, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, ArrowRight, ChevronLeft, ChevronRight } from "lucide-react";
import { accent } from "../lib/accent";

const AUTOPLAY_MS = 7000;

export default function HeroCarousel({ slides, t }) {
  const [index, setIndex] = useState(0);
  const timerRef = useRef(null);
  const total = slides.length;

  const go = useCallback(
    (next) => {
      setIndex((i) => (next + total) % total);
    },
    [total],
  );

  const startAutoplay = useCallback(() => {
    clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setIndex((i) => (i + 1) % total);
    }, AUTOPLAY_MS);
  }, [total]);

  useEffect(() => {
    startAutoplay();
    return () => clearInterval(timerRef.current);
  }, [startAutoplay]);

  const nav = (delta) => {
    go(index + delta);
    startAutoplay();
  };

  const goTo = (i) => {
    setIndex(i);
    startAutoplay();
  };

  return (
    <section
      data-testid="hero-carousel"
      className="relative -mt-20 h-[100vh] min-h-[640px] overflow-hidden bg-obsidian"
    >
      {/* Slides stacked, crossfade */}
      {slides.map((slide, i) => (
        <div
          key={i}
          className={`absolute inset-0 transition-opacity duration-[1400ms] ease-in-out ${
            i === index ? "opacity-100 z-10" : "opacity-0 z-0"
          }`}
          aria-hidden={i !== index}
        >
          {slide.contain ? (
            <>
              {/* Blurred backdrop fills the wide viewport */}
              <img
                src={slide.image}
                alt=""
                aria-hidden="true"
                className="absolute inset-0 w-full h-full object-cover scale-110"
                style={{
                  filter: `blur(40px) brightness(0.55) saturate(1.1) ${slide.filter || ""}`,
                }}
              />
              {/* Sharp full image, contained, no quality loss */}
              <img
                src={slide.image}
                alt={slide.alt || ""}
                className="absolute inset-0 w-full h-full object-contain"
                style={slide.filter ? { filter: slide.filter } : undefined}
                loading={i === 0 ? "eager" : "lazy"}
              />
            </>
          ) : (
            <img
              src={slide.image}
              alt={slide.alt || ""}
              className="absolute inset-0 w-full h-full object-cover"
              style={slide.filter ? { filter: slide.filter } : undefined}
              loading={i === 0 ? "eager" : "lazy"}
            />
          )}
          <div className="absolute inset-0 hero-overlay" />
        </div>
      ))}

      {/* Content: animate when index changes via key */}
      <div
        key={index}
        className={`relative z-20 h-full container-x flex pt-40 pb-24 md:pb-32 ${
          slides[index].verticalAlign === "top" ? "items-start pt-32 md:pt-40" : "items-end"
        }`}
      >
        <div className="grid grid-cols-12 gap-6 w-full">
          <div className="col-span-12 lg:col-span-9 space-y-8">
            <div className="flex items-center gap-4 animate-fade-in">
              <span
                className="block w-10 h-px"
                style={{ background: slides[index].accent || "#C5B358" }}
              />
              <span
                className="font-sans text-[11px] uppercase tracking-[0.22em] font-medium"
                style={{ color: slides[index].accent || "#C5B358" }}
              >
                {slides[index].eyebrow}
              </span>
            </div>
            <h1
              data-testid={`hero-title-${index + 1}`}
              className="display-h1 text-white max-w-[20ch] animate-fade-in-up delay-100"
            >
              {accent(slides[index].title)}
            </h1>
            {/* Sub-headline MUST use Montserrat (font-sans) */}
            <p
              data-testid={`hero-sub-${index + 1}`}
              className="font-sans text-lg md:text-xl lg:text-2xl text-white/85 max-w-2xl leading-relaxed font-light animate-fade-in delay-200"
            >
              {slides[index].sub}
              {slides[index].subItalic ? (
                <>
                  {" "}
                  <em className="italic text-white/85 not-italic-fallback" style={{ fontStyle: "italic", color: "rgba(255,255,255,0.85)" }}>
                    {slides[index].subItalic}
                  </em>
                </>
              ) : null}
            </p>

            <div className="flex flex-wrap gap-4 pt-4 animate-fade-in-up delay-300">
              <Link
                to={slides[index].ctaPrimaryTo || "/contact"}
                data-testid="hero-cta-primary"
                className="group gold-btn"
              >
                {slides[index].ctaPrimaryLabel || t.home.heroCta}
                <ArrowUpRight size={16} />
              </Link>
              <Link
                to={slides[index].ctaSecondaryTo || "/solutions"}
                data-testid="hero-cta-secondary"
                className="group ghost-btn"
              >
                {slides[index].ctaSecondaryLabel || t.home.heroSecondary}
                <ArrowRight size={16} />
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Left arrow */}
      <button
        type="button"
        onClick={() => nav(-1)}
        data-testid="carousel-prev"
        aria-label="Previous slide"
        className="absolute left-3 md:left-6 top-1/2 -translate-y-1/2 z-30 w-12 h-12 md:w-14 md:h-14 flex items-center justify-center border border-white/20 text-white/80 hover:text-obsidian hover:bg-champagne hover:border-champagne transition-all duration-300 backdrop-blur-sm"
      >
        <ChevronLeft size={22} />
      </button>

      {/* Right arrow */}
      <button
        type="button"
        onClick={() => nav(1)}
        data-testid="carousel-next"
        aria-label="Next slide"
        className="absolute right-3 md:right-6 top-1/2 -translate-y-1/2 z-30 w-12 h-12 md:w-14 md:h-14 flex items-center justify-center border border-white/20 text-white/80 hover:text-obsidian hover:bg-champagne hover:border-champagne transition-all duration-300 backdrop-blur-sm"
      >
        <ChevronRight size={22} />
      </button>

      {/* Slide indicators + counter */}
      <div className="absolute bottom-8 right-6 md:right-10 lg:right-16 xl:right-24 z-30 flex items-center gap-5">
        <div className="flex items-center gap-2.5">
          {slides.map((_, i) => (
            <button
              key={i}
              type="button"
              onClick={() => goTo(i)}
              data-testid={`carousel-dot-${i + 1}`}
              aria-label={`Go to slide ${i + 1}`}
              className={`transition-all duration-500 ${
                i === index
                  ? "w-10 h-[2px] bg-champagne"
                  : "w-5 h-[2px] bg-white/30 hover:bg-white/60"
              }`}
            />
          ))}
        </div>
        <span className="font-sans text-[11px] uppercase tracking-[0.22em] text-white/60 font-medium tabular-nums">
          {String(index + 1).padStart(2, "0")}
          <span className="text-white/30 mx-2">/</span>
          {String(total).padStart(2, "0")}
        </span>
      </div>

      {/* Scroll cue */}
      <div
        aria-hidden="true"
        className="absolute bottom-8 left-6 md:left-10 lg:left-16 xl:left-24 z-30 hidden md:flex items-center gap-3 text-white/50 font-sans text-[11px] uppercase tracking-[0.28em] font-medium"
      >
        <span className="block w-12 h-px bg-champagne" />
        <span>Scroll</span>
      </div>
    </section>
  );
}
